import axios from 'axios';

import { undoConstants } from '../constants';
import { reloadAfterChange } from './reloadActions';

export const undoActions = {
    undoLastChange,
    clearUndo,
};

function clearUndo() {
    return { type: undoConstants.CLEAR_UNDO };
}

/**
 * Reverts the most recent undo entry, then reloads and recalculates the
 * current scenario so the table shows the restored state.
 *
 * An entry's `undo` is one server snapshot, or an array of snapshots for
 * changes made together (e.g. one CFOPilot approval); those are undone
 * newest first, as a single step.
 *
 * @returns { label, valueIds, recalculated } of the undone entry, or null if nothing was undone
 */
function undoLastChange() {
    return async (dispatch, getState) => {
        const { stack, undoing } = getState().undo;
        const entry = stack[stack.length - 1];
        if (!entry || undoing) return null;

        dispatch(request(entry));

        const snapshots = Array.isArray(entry.undo) ? entry.undo : [entry.undo];
        let formulasChanged = false;
        const scenarioIds = [];
        const valueIds = [];
        let undone = 0;

        try {
            for (const snapshot of [...snapshots].reverse()) {
                const response = await axios.post('/undo', { undo: snapshot });
                formulasChanged = formulasChanged || response.data.kind === 'formula';
                scenarioIds.push(...(response.data.scenarioIds ?? []));
                valueIds.push(...(response.data.valueIds ?? []));
                undone += 1;
            }

            dispatch(success());

            const affectsCurrent = await dispatch(reloadAfterChange({ formulasChanged, scenarioIds }));

            // The reload sends fresh DB values to converge, so any other saved-but-not-
            // calculated edits on the current scenario are recalculated as well.
            return { label: entry.label, valueIds, recalculated: affectsCurrent };
        } catch (error) {
            console.error(error);

            const status = error.response?.status;
            const data = error.response?.data;
            const message =
                (typeof data === 'string' ? data : data?.message ?? data?.error) ??
                'Unable to undo at this moment. Please try again later.';

            // Keep what wasn't undone yet for network/server errors; drop it if the server rejected it.
            const remaining = snapshots.slice(0, snapshots.length - undone);
            const rest = Array.isArray(entry.undo) ? { ...entry, undo: remaining } : entry;
            dispatch(failure(rest, message, status == null || status >= 500));

            if (undone > 0) {
                dispatch(reloadAfterChange({ formulasChanged, scenarioIds })).catch(console.error);
                throw new Error(`Undid ${undone} of ${snapshots.length} changes. ${message}`);
            }
            throw new Error(message);
        }
    };

    function request(entry) {
        return { type: undoConstants.UNDO_REQUEST, entry };
    }
    function success() {
        return { type: undoConstants.UNDO_SUCCESS };
    }
    function failure(entry, error, restore) {
        return { type: undoConstants.UNDO_FAILURE, entry, error, restore };
    }
}
